import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Link,
  TextField,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ClearFieldButton from './ClearFieldButton';

export const normalizeUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const QUARTR_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

interface EditableFieldCellProps {
  companyId: string;
  value?: string;
  label?: string;
  canEdit: boolean;
  isUrl?: boolean;
  /** When true, existing values show an edit button. */
  allowEditExisting?: boolean;
  /** When true, show a clear button that removes the current value. */
  allowClear?: boolean;
  /** When false, empty fields show — instead of + Add URL/value. */
  allowAdd?: boolean;
  placeholder?: string;
  onSave: (companyId: string, value: string) => Promise<void>;
}

const EditableFieldCell: React.FC<EditableFieldCellProps> = ({
  companyId,
  value,
  label,
  canEdit,
  isUrl = false,
  allowEditExisting = true,
  allowClear = false,
  allowAdd = true,
  placeholder,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedValue = value?.trim() || '';
  const hasValue = Boolean(trimmedValue);

  const startEditing = (initial = '') => {
    setDraft(initial);
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const validate = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return 'Value is required';
    if (isUrl) {
      try {
        const url = normalizeUrl(trimmed);
        new URL(url);
      } catch {
        return 'Enter a valid URL';
      }
    } else if (!QUARTR_ID_PATTERN.test(trimmed)) {
      return 'Use letters, numbers, underscores, or hyphens only';
    }
    return null;
  };

  const persistValue = async (nextValue: string) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, nextValue);
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate(draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    const nextValue = isUrl ? normalizeUrl(draft) : draft.trim();
    await persistValue(nextValue);
  };

  const handleClear = async () => {
    await persistValue('');
  };

  const renderEditor = () => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25 }}>
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder || (isUrl ? 'Paste URL…' : 'Enter value…')}
        size="small"
        autoFocus
        error={Boolean(error)}
        helperText={error || undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') cancelEditing();
        }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      {saving ? (
        <CircularProgress size={18} sx={{ mt: 1, mx: 0.5 }} />
      ) : (
        <Box sx={{ display: 'flex', mt: 0.5 }}>
          <Tooltip title="Save">
            <IconButton size="small" color="success" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={cancelEditing}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  if (editing) {
    return (
      <Box onClick={(e) => e.stopPropagation()}>
        {label && (
          <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
            <strong>{label}:</strong>
          </Typography>
        )}
        {renderEditor()}
      </Box>
    );
  }

  if (hasValue) {
    const content = isUrl ? (
      <Link
        href={normalizeUrl(trimmedValue)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {trimmedValue.length > 48 ? `${trimmedValue.slice(0, 48)}…` : trimmedValue}
        </Box>
        <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, flexShrink: 0 }} />
      </Link>
    ) : (
      <Typography component="span" variant="body2">
        {trimmedValue}
      </Typography>
    );

    if (!canEdit || !allowEditExisting) {
      return label ? (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>{label}:</strong> {content}
        </Typography>
      ) : (
        <>{content}</>
      );
    }

    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
        onClick={(e) => e.stopPropagation()}
      >
        {label ? (
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            <strong>{label}:</strong> {content}
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0 }}>{content}</Box>
        )}
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => startEditing(trimmedValue)} disabled={saving}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        {allowClear && (
          <ClearFieldButton onClear={handleClear} disabled={saving} />
        )}
      </Box>
    );
  }

  if (!canEdit || !allowAdd) {
    return label ? (
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>{label}:</strong>{' '}
        <Box component="span" sx={{ color: 'text.disabled' }}>
          —
        </Box>
      </Typography>
    ) : (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    );
  }

  return (
    <Typography variant="body2" sx={{ mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
      {label && <strong>{label}: </strong>}
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => startEditing()}
        sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
      >
        + Add {isUrl ? 'URL' : 'value'}
      </Link>
    </Typography>
  );
};

export default EditableFieldCell;
